import React, { useCallback, useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import { Button } from "react-bootstrap";
import { BsArrowDown, BsDownload } from "react-icons/bs";
import { toast } from "react-toastify";
import useApiClient, { apiRoutes } from "../../../../../hooks/apiClient";
import TextSpinner from "../../../../../components/UI/Spinner/TextSpinner";
import DateTime from "../../../../../components/Formatters/DateTime";
import { downloadZip } from "../../../../../components/UI/ZipUtils/downloadZip";
import constants from "../../../../Constants";

const CheckListDocumentosPAT = ({ acaoFiscal, numOs, pageSizeRows = 10 }) => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizeRows);
  const [pageNumber, setPageNumber] = useState(1);
  const selectAllRef = useRef();
  const [selectedFiles, setSelectedFiles] = useState([]);

  const { request, data, errors, isLoading } = useApiClient();

  // ==========================
  // CONTROLE DE SELEÇÃO
  // ==========================
  const toggleFile = (file) => {
    setSelectedFiles((prev) => {
      const exists = prev.find((f) => f.id === file.id);
      return exists ? prev.filter((f) => f.id !== file.id) : [...prev, file];
    });
  };

  const getAllFilesFromRows = () => {
    const allFiles = [];

    rows.forEach((row) => {
      const documentos = row.documentos || [];
      const anexos = row.anexos || [];

      documentos.forEach((doc, index) => {
        const id = `doc-${row.sqAndamento}-${index}`;
        allFiles.push({
          id,
          nome: doc.nomeArquivo,
          base64: doc.documento,
        });
      });

      anexos.forEach((anexo, index) => {
        const id = `anexo-${row.sqAndamento}-${index}`;
        allFiles.push({
          id,
          nome: anexo.nomeArquivo,
          base64: anexo.documento,
        });
      });
    });

    return allFiles;
  };

  const allFiles = getAllFilesFromRows();

  const isAllSelected = allFiles.length > 0 && selectedFiles.length === allFiles.length;

  const isPartiallySelected = selectedFiles.length > 0 && selectedFiles.length < allFiles.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(allFiles);
    }
  };

  const isChecked = (id) => selectedFiles.some((f) => f.id === id);

  const columns = [
    // {
    //   width: "55px",
    //   selector: (row) => <CustomExpanderIcon row={row} emptyWhen={(item) => (item.anexos?.length ?? 0) === 0} />,
    // },
    {
      name: "Data Andamento",
      selector: "andamento",
      sortable: true,
      maxWidth: "15%",
      cell: (row) => <DateTime value={row.andamento} stringFormat="dd/MM/yyyy HH:mm:ss" />,
    },
    {
      name: "Atividade",
      selector: "atividade",
      sortable: true,
      maxWidth: "35%",
    },
    {
      name: "Situação",
      selector: "situacao",
      center: true,
      sortable: true,
      maxWidth: "10%",
    },
    {
      name: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="checkbox"
            ref={selectAllRef}
            checked={isAllSelected}
            onChange={toggleSelectAll}
            title={isAllSelected ? "Desmarcar todos os documentos" : "Selecionar todos os documentos"}
          />
          Documentos
        </div>
      ),
      grow: 2,
      wrap: true,
      cell: (row) => {
        const documentos = row.documentos || [];
        const anexos = row.anexos || [];

        if (!documentos.length && !anexos.length) {
          return <span style={{ color: "#999" }}>Nenhum documento</span>;
        }

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {/* DOCUMENTOS */}
            {documentos.map((doc, index) => {
              const id = `doc-${row.sqAndamento}-${index}`;
              const nome = `${doc.nomeArquivo}`;

              return (
                <label key={id} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={isChecked(id)}
                    onChange={() =>
                      toggleFile({
                        id,
                        nome,
                        base64: doc.documento,
                      })
                    }
                  />
                  {nome}
                </label>
              );
            })}

            {/* ANEXOS */}
            {anexos.map((anexo, index) => {
              const id = `anexo-${row.sqAndamento}-${index}`;

              return (
                <label
                  key={id}
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    opacity: 0.85,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked(id)}
                    onChange={() =>
                      toggleFile({
                        id,
                        nome: anexo.nomeArquivo,
                        base64: anexo.documento,
                      })
                    }
                  />
                  {anexo.nomeArquivo}
                </label>
              );
            })}
          </div>
        );
      },
    },
  ];

  // ==========================
  // PAGINAÇÃO
  // ==========================
  const handlePageChange = useCallback((page) => {
    setPageNumber(page);
  }, []);

  const handlePerRowsChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  }, []);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isPartiallySelected;
    }
  }, [isPartiallySelected]);

  // ==========================
  // BUSCA
  // ==========================
  useEffect(() => {
    if (!acaoFiscal) return;

    request(apiRoutes.fiscalizacao.ordemservico.andamentoOs, {
      acaoFiscal: acaoFiscal,
      pageSize,
      pageNumber,
    });
  }, [request, acaoFiscal, pageSize, pageNumber]);

  useEffect(() => {
    if (data?.result) {
      const apenasAtivos = data.result.filter((item) => item.situacao === "Ativa");

      setRows(apenasAtivos);
      setTotalRows(apenasAtivos.length);
    }
    if (errors) {
      toast.error(errors.message);
    }
  }, [data, errors]);

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 10, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="primary"
          disabled={!selectedFiles.length}
          onClick={() => downloadZip(selectedFiles, `documentosOS-${numOs}.zip`)}
        >
          <BsDownload /> Baixar selecionados ({selectedFiles.length})
        </Button>
      </div>

      <DataTable
        noHeader
        keyField="sqAndamento"
        columns={columns}
        data={rows}
        progressPending={isLoading}
        progressComponent={<TextSpinner />}
        noDataComponent="Nenhum registro para exibir"
        sortIcon={<BsArrowDown />}
        pagination
        paginationTotalRows={totalRows}
        paginationPerPage={pageSize}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationComponentOptions={constants.paginationComponentOptions}
      />
    </div>
  );
};

export default CheckListDocumentosPAT;
